import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getArticleBySlug, articles, getArticlesByCategory, Article } from "@/mocks/articles";
import { getCategoryName } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | undefined>();
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug) {
      const found = getArticleBySlug(slug);
      setArticle(found);
      if (found) {
        const related = getArticlesByCategory(found.category)
          .filter((a) => a.slug !== slug)
          .slice(0, 3);
        setRelatedArticles(related);
      }
    }
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl" />
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">您要查看的文章可能已被移除或链接失效。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap transition-colors duration-200"
            >
              <i className="ri-arrow-left-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                {article.categoryName}
              </span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-time-line" />
                {article.readTime}
              </span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 91.cn`}
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded bg-background-200 text-foreground-600 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/category/${article.category}`}
                className="text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap transition-colors duration-200 flex items-center gap-1"
              >
                <i className="ri-arrow-left-line" />
                返回{article.categoryName}
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
            <h2 className="text-xl font-bold text-foreground-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer border border-background-200 hover:border-primary-200 transition-all duration-200"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      title={`${related.title} - 91.cn`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1.5">{related.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "author": {
            "@type": "Person",
            "name": article.author
          },
          "datePublished": article.date,
          "image": article.image,
          "publisher": {
            "@type": "Organization",
            "name": "91.cn",
            "url": "https://www.91.cn"
          },
          "url": `https://www.91.cn/article/${article.slug}`,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://www.91.cn/article/${article.slug}`
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.91.cn"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": article.categoryName,
              "item": `https://www.91.cn/category/${article.category}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": article.title
            }
          ]
        })}
      </script>
    </>
  );
}